import React, { useState, useContext, useEffect } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { useTable, useSortBy } from "react-table";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";

import { Context } from "../../Utility/ContextManager";
import { formatDate, numberWithCommas, statusStyles } from "../../Utility";
import { SpinnerLoading } from "../../components/Loadings";
import {
  getTransactions,
  listTenantCategoryFN,
  fetchFuturePayments,
  updateRecordTB,
  fetchAllBankAccounts,
} from "../../Utility/ApiService";
import { useconfirmAlert } from "../../Utility/Confirmation";
import { Modal } from "react-bootstrap";
import { KeyboardDatePicker } from "@material-ui/pickers";
import CalenderIcon from "../../assets/images/calendar-icon.svg";

function PaymentHistory() {
  const [selectedDate, setSelectedDate] = useState([
    moment().subtract(6, "month").toISOString(),
    moment().toISOString(),
  ]);
  const [loading, setLoading] = useState(true);
  const { user, allTransactions, setAllTransactions, bankAccounts, setBankAccounts } = useContext(Context);
  const [tenantCategories, setTenantCategories] = useState([]);
  const [isEditData, setIsEditData] = useState(false);

  const [inputEditObj, setInputEditObj] = useState({
    id: "",
    inputAmount: 0,
    selectedDate: new Date(),
    selectedBankAccount: "",
  });

  const columns = React.useMemo(
    () => [
      {
        Header: "Transaction Number",
        accessor: "transaction_payment_id",
      },
      {
        Header: "Account",
        accessor: (row) =>
          row?.payment_mode === "BANK" || row.status === "SCHEDULED"
            ? `${row?.nick_name || row?.bank_name || ""}...${row?.account_number || ""}`
            : `Cash`,
      },
      {
        Header: "Date",
        accessor: (row) => formatDate(row?.date),
      },
      {
        Header: "Note",
        accessor: "note",
      },
      {
        Header: "Category",
        accessor: "category",
      },
      {
        Header: "Amount",
        accessor: (row) => (
          <label
            style={{
              color: row?.member_auto_payment === 1 ? "#539488" : "",
            }}
          >
            {row?.member_auto_payment === 1 ? "Full Balance" : `$${numberWithCommas(row?.transaction_amount)}`}
          </label>
        ),
      },
      {
        Header: "Status",
        accessor: (row) => {
          const isDebitCharge = row?.status === "COMPLETED" && row?.transaction_type === "DEBIT";
          return (
            <label
              className={`w-100 d-flex align-items-center justify-content-center payment-status ${
                row?.repeat_payment === 1 || row?.member_auto_payment === 1
                  ? "badge bg-auto-pay"
                  : row?.status === "CANCELLED"
                  ? "badge bg-danger"
                  : row?.status === "PENDING"
                  ? " badge bg-gray"
                  : isDebitCharge
                  ? "badge bg-danger"
                  : row?.status === "COMPLETED"
                  ? "badge bg-success"
                  : row?.status === "SCHEDULED"
                  ? "badge bg-secondary"
                  : row?.status === "FAILED"
                  ? "badge bg-danger"
                  : ""
              }`}
              style={{
                width: row?.status === "PENDING" ? "150px" : "105px",
                height: "35px",
              }}
            >
              {row?.repeat_payment === 1 || row?.member_auto_payment === 1
                ? "Scheduled Autopay"
                : row?.status === "PENDING"
                ? "Pending"
                : statusStyles(row?.status, row?.transaction_type)?.text || ""}
            </label>
          );
        },
      },
      {
        Header: " ", // Added header for Actions column
        accessor: (row) => (
          <>
            {row?.status === "SCHEDULED" && (
              <div className="dropdown text-end">
                <button
                  className="btn dropdown-toggle"
                  type="button"
                  id="dropdownMenuButton1"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  disabled={row?.status !== "SCHEDULED"}
                >
                  <img src={require("../../assets/images/toggle-btn.svg").default} />
                </button>
                <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                  {row?.member_auto_payment !== 1 && (
                    <li
                      className="dropdown-item"
                      onClick={() => {
                        setIsEditData(true);
                        setInputEditObj({
                          id: row?.lease_payment_id,
                          inputAmount: row?.transaction_amount,
                          selectedDate: formatDate(row?.date),
                          selectedBankAccount: row?.bank_account_id,
                          status: row?.status,
                        });
                      }}
                    >
                      <img src={require("../../assets/images/edit.svg").default} />
                      <span>Edit</span>
                    </li>
                  )}
                  <li
                    className="dropdown-item "
                    onClick={() =>
                      useconfirmAlert({
                        title: "This will cancel your transaction, are you sure?",
                        onConfirm: () => handleDelete(row),
                      })
                    }
                  >
                    <img src={require("../../assets/images/delete.svg").default} alt="" />
                    <span className="text-danger">Delete</span>
                  </li>
                </ul>
              </div>
            )}
          </>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    getAllTransactions();
    getAllBankAccounts();
  }, [selectedDate]);

  const handleDelete = async (item) => {
    try {
      if (item?.member_auto_payment === 1) {
        const memberLeasePaymentObj = {
          id: item?.member_auto_payment_id,
          active: 0,
        };
        setLoading(true);
        await updateRecordTB("MemberAutoPayment", memberLeasePaymentObj);
        await getAllTransactions();
        toast.success("Payment deleted successfully.");
        setLoading(false);
      } else {
        if (item?.status === "SCHEDULED") {
          // const hours = moment(item.next_occurance).diff(moment(), "hours");
          // if (hours >= 12) {
          setLoading(true);
          const bankAccountObj = {
            id: item.lease_payment_id,
            active: 0,
          };
          await updateRecordTB("PropertyLeasePayment", bankAccountObj);
          await getAllTransactions();
          toast.success("Payment deleted successfully.");
          setLoading(false);
          // } else {
          //   toast.error("This payment can not delete before 24 hours.");
          // }
        }
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error(error.errorMessage || error.message || "Something went wrong");
    }
  };

  const getAllBankAccounts = async () => {
    try {
      setLoading(true);
      const data = await fetchAllBankAccounts({ tenant_id: user?.id });
      // Set state for bank accounts and upcoming transactions with photos
      setBankAccounts(data);
      setLoading(false);
    } catch (error) {
      console.log("error getAllBankAccounts ", error);
      setLoading(false);
      toast.error(error.errorMessage || error.message || error);
    }
  };

  const getAllTransactions = async () => {
    try {
      setLoading(true);
      let tenantCategories = await listTenantCategoryFN();
      tenantCategories = tenantCategories?.sort((a, b) => a?.category.localeCompare(b?.category));
      setTenantCategories(tenantCategories);

      const transactions = await getTransactions({
        tenantId: user?.id,
        startDate: selectedDate[0],
        endDate: selectedDate[1],
      });

      const formattedTransactions = transactions?.map((transaction) => ({
        ...transaction,
        category: tenantCategories?.find((category) => category.id === transaction?.category_id)?.category,
      }));

      const futurePayments = await fetchFuturePayments({ tenant_id: user?.id });

      const formattedFuturePayments = futurePayments?.map((payment) => ({
        ...payment,
        transaction_payment_id: payment?.lease_payment_id,
        payment_mode: payment?.payment_mode,
        nick_name: payment?.nick_name,
        account_number: payment?.account_number,
        bank_name: payment?.bank_name,
        date: payment?.next_occurance,
        transaction_amount: payment?.payment_amount,
        status: "SCHEDULED",
        category: payment?.member_auto_payment === 1 ? "Common Charges" : payment?.category,
        member_auto_payment: payment?.member_auto_payment || 0,
        repeat_payment: payment?.repeat_payment || 0,
      }));
      if (formattedTransactions) setAllTransactions([...formattedFuturePayments, ...formattedTransactions]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("fetchAllMaintenances ", error);
      toast.error(error.message || "Something went wrong please try again later");
    }
  };

  const onDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleEdit = async (row) => {
    try {
      if (inputEditObj?.status === "SCHEDULED") {
        setLoading(true);
        const bankAccountObj = {
          id: inputEditObj.id,
          payment_amount: inputEditObj.inputAmount,
          next_occurance: moment(inputEditObj.selectedDate).format("YYYY-MM-DD"),
          last_modified: moment().format("YYYY-MM-DD HH:MM:SS"),
          bank_account_id: Number(inputEditObj.selectedBankAccount),
          // payment_category: selectedType,
        };
        await updateRecordTB("PropertyLeasePayment", bankAccountObj);
        await getAllTransactions();
        setIsEditData(false);

        setLoading(false);
        toast.success("Transaction updated successfully.");
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error(error.errorMessage || error.message || "Something went wrong");
    }
  };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: allTransactions,
    },
    useSortBy
  );

  const CardView = () => {
    return (
      <div className="row">
        {allTransactions.map((transaction, index) => (
          <div key={index} className="col-md-6 mb-4">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <label className="card-text">{transaction.transaction_payment_id}</label>
                  <span
                    className={`w-50 d-flex align-items-center justify-content-center payment-status ${
                      transaction?.repeat_payment === 1 || transaction?.member_auto_payment === 1
                        ? "badge bg-auto-pay"
                        : transaction?.status === "CANCELLED"
                        ? "badge bg-danger"
                        : transaction?.status === "PENDING"
                        ? " badge bg-gray"
                        : transaction?.status === "COMPLETED" && transaction?.transaction_type === "DEBIT"
                        ? "badge bg-danger"
                        : transaction?.status === "COMPLETED"
                        ? "badge bg-success"
                        : transaction?.status === "SCHEDULED"
                        ? "badge bg-secondary"
                        : transaction?.status === "FAILED"
                        ? "badge bg-danger"
                        : ""
                    }`}
                  >
                    {transaction?.repeat_payment === 1 || transaction?.member_auto_payment === 1
                      ? "Scheduled Autopay"
                      : statusStyles(transaction?.status, transaction?.transaction_type)?.text}
                  </span>
                </div>
                <p className="card-text">
                  <label className="gray-text">Account:</label>{" "}
                  {transaction.payment_mode === "BANK"
                    ? `${transaction.nick_name || transaction.bank_name || ""}...${transaction.account_number || ""}`
                    : `Cash`}
                </p>
                <p>
                  <label className="gray-text">Category:</label> {transaction?.category}
                </p>
                <p className="card-text">
                  <label className="gray-text">Note:</label> {transaction.note}
                </p>
                <p className="card-text">
                  <label className="gray-text">Date:</label> {formatDate(transaction.date)}
                </p>
                <p>
                  <label className="gray-text">Amount:</label> ${numberWithCommas(transaction.transaction_amount)}
                </p>
                {transaction?.status === "SCHEDULED" && (
                  <div className="d-flex justify-content-around">
                    {transaction?.member_auto_payment !== 1 && (
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setIsEditData(true);
                          setInputEditObj({
                            id: transaction?.lease_payment_id,
                            inputAmount: transaction?.transaction_amount,
                            selectedDate: formatDate(transaction?.date),
                            selectedBankAccount: transaction?.bank_account_id,
                            status: transaction?.status,
                          });
                        }}
                      >
                        <img src={require("../../assets/images/edit.svg").default} />
                        <span>Edit</span>
                      </div>
                    )}
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        useconfirmAlert({
                          title: "This will cancel your transaction, are you sure?",
                          onConfirm: () => handleDelete(transaction),
                        })
                      }
                    >
                      <img className="pointer" src={require("../../assets/images/delete.svg").default} alt="" />
                      <span className="text-danger pointer">Delete</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {allTransactions.length === 0 && (
          <div className="col-12">
            <div className="no-data-found text-center">
              <div className="svg-frame">
                <img src={require("../../assets/images/frame.svg").default} alt="frame" />
              </div>
              <p>So far, you have not had any payments.</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const TableView = () => {
    return (
      <div className="table-responsive">
        <table {...getTableProps()} className="table customise-table cell-border w-100">
          <thead>
            {headerGroups?.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup?.headers.map((column) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column?.render("Header")}
                    <span style={{ color: "#8C8C8C", marginLeft: "12px" }}>
                      {column?.isSorted ? (
                        column?.isSortedDesc ? (
                          <i className="fa fa-caret-down"></i>
                        ) : (
                          <i className="fa fa-caret-up"></i>
                        )
                      ) : (
                        ""
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows?.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row?.cells.map((cell) => {
                    return <td {...cell.getCellProps()}>{cell?.render("Cell")}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      {loading && (
        <div className="loadingSpinner">
          <SpinnerLoading />
        </div>
      )}
      <div className="row">
        <div className="col-xl-5 col-lg-4">
          <div className="page-title">
            <h2>Transaction History</h2>
          </div>
        </div>

        <div className="col-xl-7 col-lg-8 col-sm-12">
          <div className="d-flex flex-column flex-md-row justify-content-lg-end mb-4">
            <div className="date-range customise-date-range mb-3">
              <DateRangePicker
                className="customise-dateRangePicker"
                onChange={onDateChange}
                value={selectedDate}
                maxDate={new Date()}
                clearIcon={false}
                closeCalendar={false}
                format="MM-dd-y"
              />
            </div>
            <div className="m-0 ms-md-3">
              <button onClick={() => window.print()} className="btn btn-primary btn-print">
                <img src={require("../../assets/images/print-icon.svg").default} /> Print
              </button>
            </div>
          </div>
        </div>
      </div>
      {rows.length > 0 ? (
        <>
          <div className="d-md-none">
            <CardView />
          </div>
          <div className="d-none d-md-block">
            <TableView />
          </div>
        </>
      ) : (
        <div className="no-data-found text-center">
          <div className="svg-frame">
            <img src={require("../../assets/images/frame.svg").default} alt="frame" />
          </div>
          <p>So far, you have not had any payments.</p>
        </div>
      )}
      <Modal className="modal-style-1" centered show={isEditData} onHide={() => setIsEditData(false)}>
        <Modal.Body>
          <h4 className="mb-0 modal-header">Edit Schedule Payment</h4>

          <div className="accordion accordion-flush accordion-v2" id="accordion1">
            <div className="accordion-item">
              <div className="mt-3">
                <label className="mb-2">Select Payment Date</label>
                <KeyboardDatePicker
                  value={inputEditObj.selectedDate}
                  className="form-control"
                  format="MM/DD/YYYY"
                  onChange={(date) => setInputEditObj({ ...inputEditObj, selectedDate: date })}
                  variant="inline" //static
                  disableToolbar
                  autoOk
                  InputAdornmentProps={{ position: "end" }}
                  keyboardIcon={<img src={CalenderIcon} />}
                  disablePast
                  error={false}
                  helperText={null}
                />
              </div>

              <div className="mt-3">
                <label className="mb-2">Amount</label>
                <input
                  className="form-control"
                  type="number"
                  //disabled={true}
                  value={inputEditObj.inputAmount}
                  onChange={(e) => setInputEditObj({ ...inputEditObj, inputAmount: Number(e.target.value) })}
                />
              </div>
            </div>
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingTwo">
                <button
                  className="accordion-button collapsed schedule-date-btn"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseTwo"
                  aria-expanded="false"
                  aria-controls="collapseTwo"
                >
                  Pay From
                </button>
              </h2>
              <div
                id="collapseTwo"
                className="accordion-collapse collapse"
                aria-labelledby="headingTwo"
                data-bs-parent="#accordion1"
              >
                <div className="accordion-body">
                  <div className="select">
                    <select
                      defaultValue="hide"
                      onChange={(e) => {
                        e.preventDefault();
                        if (e.target.value) {
                          setInputEditObj({ ...inputEditObj, selectedBankAccount: e.target.value });
                        }
                      }}
                      value={inputEditObj.selectedBankAccount}
                    >
                      <option disabled value="hide">
                        {bankAccounts.length > 0 ? "Select Bank Account" : "You have not added any bank account"}
                      </option>

                      {bankAccounts?.map((bank, index) => (
                        <option key={index} value={bank.id}>
                          **** **** **** {bank?.account_number}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row modal-btns">
            <div className="col-6">
              <button type="button" className="btn btn-warning w-100" onClick={() => setIsEditData(false)}>
                Cancel
              </button>
            </div>
            <div className="col-6">
              <button
                disabled={!inputEditObj.selectedBankAccount}
                onClick={handleEdit}
                type="button"
                className="btn btn-primary w-100"
              >
                Continue
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default PaymentHistory;
